import lodashGet from 'lodash/get';
import { api } from '@/api/api';
import { apis, inWindow } from '@/config';
import { util, wait } from '@/api/util';
import ContactUtilInterface from './contact_util';
import { ContactDBInstance } from './contact_dbl';
export class ContactHealthDetectHelper {
    constructor() {
        this.contactUtil = ContactUtilInterface;
        this.eventApi = api.getEventApi();
        this.detectStatusMap = new Map();
        this.contactDB = ContactDBInstance;
        this.detectIsExistIds = new Set();
        this.orgContactlimitSize = 5000;
        this.expiredSpan = 5 * 24 * 60 * 60 * 1000;
        this.orgContactDuplicateMap = new Map();
        this.contactItemtDuplicateMap = new Map();
        this.intervalContactReadyHandler = 0;
        this.isContactReady = false;
        this.systemApi = api.getSystemApi();
        this.dbApi = api.requireLogicalApi(apis.dbInterfaceApiImpl);
        this.dataTrackerApi = api.requireLogicalApi(apis.dataTrackerApiImp);
        if (inWindow()) {
            this.intervalContactReadyHandler = this.systemApi.intervalEvent({
                id: 'contactHealthDetect-intervalContactStatus',
                handler: async () => {
                    const status = await this.contactDB.detectCoreEnterpriseHasData({ from: 'enterprise' });
                    if (status === 'all') {
                        this.isContactReady = true;
                        this.systemApi.cancelEvent('long', this.intervalContactReadyHandler);
                    }
                },
                eventPeriod: 'long',
                seq: 0,
            });
        }
    }
    async detectContactData() {
        try {
            const enableContinue = await this.doDetectContactComplete('checkContactComplete');
            if (enableContinue) {
                return;
            }
        }
        catch (ex) {
            console.error('[healthdetect].error0', ex);
        }
        try {
            const enableContinue = await this.doDetectOrgContactData(this.contactUtil.orgContactTable);
            if (!enableContinue) {
                return;
            }
        }
        catch (ex) {
            console.error('[healthdetect].error1', ex);
        }
        try {
            await this.doDetectContactItemData(this.contactUtil.contactItemTable);
        }
        catch (ex) {
            console.error('[healthdetect].error2', ex);
        }
    }
    async doDetectContactComplete(taskName, triggerType = 'auto') {
        var _a;
        if (this.detectStatusMap.get('checkContactComplete') === 'ing') {
            return false;
        }
        this.detectStatusMap.set('checkContactComplete', 'ing');
        const { expiredTime } = await this.queryContactDetectTask(taskName);
        if (expiredTime > Date.now()) {
            this.detectStatusMap.set('checkContactComplete', 'done');
            return true;
        }
        const startTime = Date.now();
        const [contactList, contactItemList] = await Promise.all([
            this.dbApi.getByRangeCondition({
                dbName: this.contactUtil.contactDbName,
                tableName: this.contactUtil.contactTable,
            }),
            this.dbApi.getByEqCondition({
                dbName: this.contactUtil.contactDbName,
                tableName: this.contactUtil.contactItemTable,
                query: {
                    contactItemType: 'EMAIL',
                },
            }),
        ]);
        const contactCount = contactList.filter(item => item.type === 'enterprise').length;
        const contactItemCount = contactItemList.filter(item => item.type === 'enterprise').length;
        const eventName = process.env.BUILD_ISELECTRON ? 'detectContactException' : 'detectContactExceptionInWeb';
        if (contactCount !== contactItemCount) {
            console.log('[contact_healthdetect_helper]doDetectContactComplete:doSomething', triggerType);
            this.dataTrackerApi.track('pc_contact_check_contactitem_complete', {
                type: triggerType,
                contactCount,
                contactItemCount,
                enableTrackInBg: true,
            });
            this.eventApi.sendSysEvent({
                eventName,
                eventData: {
                    type: 'contactNotEqualContactItem',
                    enableSkipFullSync: true,
                },
                _account: (_a = this.systemApi.getCurrentUser()) === null || _a === void 0 ? void 0 : _a.id,
            });
        }
        this.detectStatusMap.set('checkContactComplete', 'done');
        const endTime = Date.now();
        return endTime - startTime < 2000;
    }
    async doDetectContactItemData(taskName = this.contactUtil.contactItemTable) {
        if (this.detectStatusMap.get(taskName) === 'ing') {
            return false;
        }
        this.detectStatusMap.set(taskName, 'ing');
        const { expiredTime } = await this.queryContactDetectTask(taskName);
        if (expiredTime > Date.now()) {
            this.detectStatusMap.set(taskName, 'done');
            return true;
        }
        const startTime = Date.now();
        await this.doQueryDuplicateContactItem({
            offset: 0,
            limit: this.orgContactlimitSize,
        });
        await this.doDeleteValidContactItem();
        this.storeContactDetectTask(taskName, {
            expiredTime: Date.now() + this.expiredSpan,
            isDone: true,
        });
        const endTime = Date.now();
        this.detectStatusMap.set(taskName, 'done');
        return endTime - startTime < 2000;
    }
    async doDetectOrgContactData(taskName) {
        if (this.detectStatusMap.get(taskName) === 'ing') {
            return false;
        }
        this.detectStatusMap.set(taskName, 'ing');
        const { expiredTime } = await this.queryContactDetectTask(taskName);
        if (expiredTime > Date.now()) {
            this.detectStatusMap.set(taskName, 'done');
            return true;
        }
        const startTime = Date.now();
        await this.doQueryDuplicateOrgContact({
            offset: 0,
            limit: this.orgContactlimitSize,
        });
        await this.doDeleteDuplicateOrgContact();
        this.storeContactDetectTask(this.contactUtil.orgContactTable, {
            expiredTime: Date.now() + this.expiredSpan,
            isDone: true,
        });
        const endTime = Date.now();
        this.detectStatusMap.set(taskName, 'done');
        return endTime - startTime < 2000;
    }
    async storeContactDetectTask(taskName, taskResult) {
        var _a, _b;
        const domain = ((_a = this.systemApi.getCurrentUser()) === null || _a === void 0 ? void 0 : _a.domain) || '';
        const account = ((_b = this.systemApi.getCurrentUser()) === null || _b === void 0 ? void 0 : _b.accountMd5) || '';
        const id = util.getUnique(domain, account, taskName);
        this.dbApi.put({
            dbName: 'task_global',
            tableName: 'contact_detecttask',
        }, {
            id,
            domain,
            account,
            tablename: taskName,
            expiredTime: taskResult.expiredTime,
            isDone: taskResult.isDone,
        });
    }
    async queryContactDetectTask(taskName) {
        var _a, _b;
        const domain = ((_a = this.systemApi.getCurrentUser()) === null || _a === void 0 ? void 0 : _a.domain) || '';
        const account = ((_b = this.systemApi.getCurrentUser()) === null || _b === void 0 ? void 0 : _b.accountMd5) || '';
        const tasklist = await this.dbApi.getByRangeCondition({
            dbName: 'task_global',
            tableName: 'contact_detecttask',
            adCondition: {
                type: 'equals',
                field: ['domain', 'account', 'tablename'],
                args: [[domain, account, taskName]],
            },
        });
        const isDone = lodashGet(tasklist, '[0].isDone', true);
        const expiredTime = lodashGet(tasklist, '[0].expiredTime', 0);
        return {
            isDone,
            expiredTime,
        };
    }
    async doQueryDuplicateContactItem(options) {
        const { offset = 0, limit } = options;
        const result = await this.dbApi.getByRangeCondition({
            dbName: this.contactUtil.contactDbName,
            tableName: this.contactUtil.contactItemTable,
            start: offset,
            count: limit,
        });
        result.forEach(item => {
            const { contactId, _lastUpdateTime = 0, id, type, contactItemType } = item;
            if (type !== 'enterprise' || contactItemType !== 'EMAIL') {
                return;
            }
            if (!this.contactItemtDuplicateMap.has(contactId)) {
                this.contactItemtDuplicateMap.set(contactId, {});
            }
            const details = this.contactItemtDuplicateMap.get(contactId);
            details[id] = _lastUpdateTime;
        });
        if (result.length < limit) {
            return;
        }
        await wait(2000);
        await this.doQueryDuplicateContactItem({
            offset: offset + limit,
            limit,
        });
    }
    async doQueryDuplicateOrgContact(options) {
        const { offset = 0, limit } = options;
        const mainCompanyId = this.systemApi.getCurrentCompanyId();
        const result = await this.dbApi.getByRangeCondition({
            dbName: this.contactUtil.contactDbName,
            tableName: this.contactUtil.orgContactTable,
            start: offset,
            count: limit,
        });
        result.forEach(item => {
            const { enterpriseId: _enterpriseId, orgId, id, contactId, _lastUpdateTime = 0 } = item;
            if (_enterpriseId && _enterpriseId !== mainCompanyId) {
                return;
            }
            if (orgId && !/^\d+/.test(orgId)) {
                return;
            }
            const virtualId = util.getUnique(contactId, orgId);
            if (!this.orgContactDuplicateMap.has(virtualId)) {
                this.orgContactDuplicateMap.set(virtualId, {});
            }
            this.orgContactDuplicateMap.get(virtualId)[id] = _lastUpdateTime;
        });
        if (result.length < limit) {
            return;
        }
        await wait(2000);
        await this.doQueryDuplicateOrgContact({
            offset: offset + limit,
            limit,
        });
    }
    async doDeleteValidContactItem() {
        const inValidIds = [];
        this.contactItemtDuplicateMap.forEach(details => {
            if (details.size <= 1) {
                return;
            }
            const maxLastUpdateTime = Math.max(...Object.values(details));
            Object.keys(details).forEach(id => {
                if (details[id] < maxLastUpdateTime) {
                    inValidIds.push(id);
                }
            });
        });
        if (inValidIds && inValidIds.length) {
            this.dbApi.deleteById({
                dbName: this.contactUtil.contactDbName,
                tableName: this.contactUtil.contactItemTable,
            }, inValidIds);
            this.dataTrackerApi.track('pc_contact_check_duplicatedata', {
                tableName: 'contactItem',
                count: inValidIds.length,
                type: 'interval',
                enableTrackInBg: true,
            });
        }
    }
    async doDeleteDuplicateOrgContact() {
        const duplicateInvalidIds = [];
        this.orgContactDuplicateMap.forEach(item => {
            if (Object.keys(item).length <= 1) {
                return;
            }
            let maxIdKey = '';
            let maxLastUpdateTime = 0;
            Object.keys(item).forEach(key => {
                const curTime = item[key] || 0;
                maxLastUpdateTime = Math.max(maxLastUpdateTime, curTime);
                if (maxLastUpdateTime === curTime) {
                    maxIdKey = key;
                }
            });
            Reflect.deleteProperty(item, maxIdKey);
            duplicateInvalidIds.push(...Object.keys(item));
        });
        if (!duplicateInvalidIds || !duplicateInvalidIds.length) {
            return;
        }
        console.error('[contactHealthDetectHelper]删除重复数据', duplicateInvalidIds);
        this.dataTrackerApi.track('pc_contact_check_duplicatedata', {
            tableName: 'orgContact',
            count: duplicateInvalidIds.length,
            type: 'interval',
            enableTrackInBg: true,
        });
        await this.dbApi.deleteById({
            dbName: this.contactUtil.contactDbName,
            tableName: this.contactUtil.orgContactTable,
        }, duplicateInvalidIds);
    }
    async shouldUpdateContact(list) {
        if (!this.isContactReady) {
            return true;
        }
        this.detectIsExistIds = new Set([...this.detectIsExistIds, ...list.map(item => item.contact.id)]);
        if (this.detectIsExistIds.size < 10) {
            return true;
        }
        this.detectIsExistIds.clear();
        return this.doDetectContactComplete('checkContactComplete', 'user');
    }
}
export const contactHealthDetectHelper = new ContactHealthDetectHelper();
